<template>
    <b-card class="h-100">
        <h6 v-if="!embedded" class="font-weight-bold mb-1">{{ title }}</h6>
        <div v-if="subtitle" class="text-muted small mb-2">{{ subtitle }}</div>
        <div v-else-if="!embedded" class="mb-2"></div>

        <div v-if="loading" class="text-center py-4">
            <b-spinner small type="grow"></b-spinner>
        </div>
        <div v-else-if="!items.length" class="text-muted small py-3">No data available.</div>
        <template v-else>
            <div
                ref="list"
                class="velocity-list velocity-list--clickable"
                :class="{ 'velocity-list--scrollable': scrollable }"
                role="button"
                tabindex="0"
                title="Click to view all and export"
                @click="openDetailModal"
                @keydown.enter.prevent="openDetailModal"
                @keydown.space.prevent="openDetailModal"
            >
                <div
                    v-for="item in displayItems"
                    :key="item.customer_id"
                    :ref="'row-' + item.customer_id"
                    class="velocity-list__row"
                    :class="{ 'velocity-list__row--center-marker': isCenterItem(item) }"
                    :data-customer-id="item.customer_id"
                >
                    <div class="velocity-list__name">{{ item.customer_name }}</div>
                    <div class="velocity-list__value" :class="valueClass(item)">
                        <slot name="value" :item="item">{{ formatDefault(item) }}</slot>
                    </div>
                </div>
            </div>
            <div class="velocity-list__footer text-muted small mt-2">
                <span v-if="hasMorePreview">Showing {{ displayItems.length }} of {{ items.length }} · </span>
                <a href="#" class="velocity-list__view-all" @click.prevent="openDetailModal">
                    View all &amp; export
                </a>
            </div>
        </template>

        <b-modal
            :id="modalId"
            :title="title"
            size="lg"
            scrollable
            ok-only
            ok-title="Close"
            ok-variant="secondary"
            @hidden="showModal = false"
        >
            <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div class="text-muted small mb-2 mb-sm-0">
                    <span v-if="subtitle">{{ subtitle }} · </span>
                    {{ items.length }} customer{{ items.length === 1 ? '' : 's' }}
                </div>
                <b-button size="sm" variant="outline-success" :disabled="!items.length" @click="exportExcel">
                    <i class="fas fa-file-excel mr-1"></i> Export Excel
                </b-button>
            </div>

            <b-table-simple small responsive hover bordered sticky-header="calc(100vh - 280px)" class="mb-0">
                <b-thead>
                    <b-tr>
                        <b-th v-for="column in detailColumns" :key="column.key">{{ column.label }}</b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr
                        v-for="item in items"
                        :key="'modal-' + item.customer_id"
                        :class="{ 'table-primary': isCenterItem(item) }"
                    >
                        <b-td v-for="column in detailColumns" :key="column.key" :class="detailCellClass(item, column)">
                            {{ formatDetailCell(item, column) }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-modal>
    </b-card>
</template>

<script>
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
    name: 'VelocityListCard',
    props: {
        title: { type: String, required: true },
        subtitle: { type: String, default: '' },
        items: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        mode: { type: String, default: 'growth' },
        embedded: { type: Boolean, default: false },
        scrollable: { type: Boolean, default: false },
        centerOn: { type: Number, default: null },
        centerField: { type: String, default: 'lapse_ratio' },
        previewLimit: { type: Number, default: 10 },
    },
    data() {
        return {
            centerCustomerId: null,
            showModal: false,
        }
    },
    computed: {
        modalId() {
            return 'velocity-detail-' + this.mode + '-' + this._uid
        },
        displayItems() {
            if (this.scrollable || this.previewLimit <= 0 || this.items.length <= this.previewLimit) {
                return this.items
            }

            return this.items.slice(0, this.previewLimit)
        },
        hasMorePreview() {
            return !this.scrollable && this.items.length > this.displayItems.length
        },
        detailColumns() {
            if (this.mode === 'growth') {
                return [
                    { key: 'customer_name', label: 'Customer' },
                    { key: 'previous_total', label: 'Previous Total', format: 'currency' },
                    { key: 'current_total', label: 'Current Total', format: 'currency' },
                    { key: 'growth_pct', label: 'Growth %', format: 'growth_pct', colored: true },
                ]
            }

            if (this.mode === 'lapse') {
                return [
                    { key: 'customer_name', label: 'Customer' },
                    { key: 'days_since_last_order', label: 'Days Since Last Order', format: 'number' },
                    { key: 'avg_days_between_orders', label: 'Avg Days Between Orders', format: 'number' },
                    { key: 'lapse_ratio', label: 'Lapse Ratio', format: 'ratio', colored: true },
                ]
            }

            return [
                { key: 'customer_name', label: 'Customer' },
                { key: 'previous', label: 'Previous SKUs', format: 'number' },
                { key: 'current', label: 'Current SKUs', format: 'number' },
                { key: 'delta', label: 'Change', format: 'delta', colored: true },
            ]
        },
        exportSheetName() {
            if (this.mode === 'growth') return 'Order Growth'
            if (this.mode === 'lapse') return 'Lapse Ratio'
            return 'SKU Breadth'
        },
        exportFileName() {
            const stamp = new Date().toISOString().slice(0, 10)
            if (this.mode === 'growth') return 'Customer_Order_Growth_' + stamp + '.xlsx'
            if (this.mode === 'lapse') return 'Lapse_Ratio_' + stamp + '.xlsx'
            return 'SKU_Breadth_' + stamp + '.xlsx'
        },
    },
    watch: {
        items: {
            handler() {
                this.$nextTick(() => {
                    this.scrollToCenter()
                })
            },
            deep: true,
        },
        loading(value) {
            if (!value) {
                this.$nextTick(() => {
                    this.scrollToCenter()
                })
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.scrollToCenter()
        })
    },
    methods: {
        openDetailModal() {
            if (!this.items.length) {
                return
            }

            this.showModal = true
            this.$nextTick(() => {
                this.$bvModal.show(this.modalId)
            })
        },
        isCenterItem(item) {
            return this.centerCustomerId != null && item.customer_id === this.centerCustomerId
        },
        findCenterItem() {
            if (this.centerOn == null || !this.items.length) {
                return null
            }

            let best = null
            let bestDistance = Number.POSITIVE_INFINITY

            this.items.forEach((item) => {
                const value = Number(item[this.centerField])
                if (Number.isNaN(value)) {
                    return
                }

                const distance = Math.abs(value - this.centerOn)
                if (distance < bestDistance) {
                    bestDistance = distance
                    best = item
                }
            })

            return best
        },
        scrollToCenter() {
            if (!this.scrollable || this.centerOn == null || !this.$refs.list) {
                return
            }

            const centerItem = this.findCenterItem()
            if (!centerItem) {
                return
            }

            this.centerCustomerId = centerItem.customer_id

            this.$nextTick(() => {
                const list = this.$refs.list
                const row = list.querySelector('[data-customer-id="' + centerItem.customer_id + '"]')
                if (!list || !row) {
                    return
                }

                const listRect = list.getBoundingClientRect()
                const rowRect = row.getBoundingClientRect()
                const offset = (rowRect.top - listRect.top) + list.scrollTop - (list.clientHeight / 2) + (row.clientHeight / 2)
                list.scrollTop = Math.max(0, offset)
            })
        },
        valueClass(item) {
            if (this.mode === 'growth') {
                if (item.growth_pct > 0) return 'text-success'
                if (item.growth_pct < 0) return 'text-danger'
                return 'text-muted'
            }

            if (this.mode === 'breadth') {
                if (item.delta > 0) return 'text-success'
                if (item.delta < 0) return 'text-danger'
                return 'text-muted'
            }

            if (this.mode === 'lapse') {
                if (item.lapse_ratio >= 2) return 'text-danger'
                if (item.lapse_ratio >= 1.25) return 'text-warning'
                if (item.lapse_ratio < 0.85) return 'text-success'
                return 'text-muted'
            }

            return ''
        },
        formatDefault(item) {
            if (this.mode === 'growth') {
                const sign = item.growth_pct > 0 ? '+' : ''
                return sign + item.growth_pct + '%'
            }

            if (this.mode === 'lapse') {
                return item.lapse_ratio
            }

            return ''
        },
        detailCellClass(item, column) {
            if (!column.colored) {
                return ''
            }

            return this.valueClass(item)
        },
        formatDetailCell(item, column) {
            const value = item[column.key]

            if (column.format === 'currency') {
                return this.formatCurrency(value)
            }

            if (column.format === 'growth_pct') {
                const sign = value > 0 ? '+' : ''
                return sign + value + '%'
            }

            if (column.format === 'delta') {
                return value > 0 ? '+' + value : String(value)
            }

            if (column.format === 'ratio' || column.format === 'number') {
                return value == null ? '—' : value
            }

            return value ?? '—'
        },
        formatCurrency(value) {
            const amount = Number(value) || 0
            return '$' + amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        buildExportRows() {
            if (this.mode === 'growth') {
                return this.items.map((item) => ({
                    Customer: item.customer_name,
                    'Previous Total': Number(item.previous_total) || 0,
                    'Current Total': Number(item.current_total) || 0,
                    'Growth %': (Number(item.growth_pct) || 0) / 100,
                }))
            }

            if (this.mode === 'lapse') {
                return this.items.map((item) => ({
                    Customer: item.customer_name,
                    'Days Since Last Order': Number(item.days_since_last_order) || 0,
                    'Avg Days Between Orders': Number(item.avg_days_between_orders) || 0,
                    'Lapse Ratio': Number(item.lapse_ratio) || 0,
                }))
            }

            return this.items.map((item) => ({
                Customer: item.customer_name,
                'Previous SKUs': Number(item.previous) || 0,
                'Current SKUs': Number(item.current) || 0,
                Change: Number(item.delta) || 0,
            }))
        },
        applyExcelFormats(worksheet) {
            const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1')

            for (let row = range.s.r + 1; row <= range.e.r; row++) {
                if (this.mode === 'growth') {
                    this.setCellNumberFormat(worksheet, row, 1, '$#,##0.00')
                    this.setCellNumberFormat(worksheet, row, 2, '$#,##0.00')
                    this.setCellNumberFormat(worksheet, row, 3, '0.0%')
                } else if (this.mode === 'lapse') {
                    this.setCellNumberFormat(worksheet, row, 1, '0')
                    this.setCellNumberFormat(worksheet, row, 2, '0.0')
                    this.setCellNumberFormat(worksheet, row, 3, '0.00')
                } else {
                    this.setCellNumberFormat(worksheet, row, 1, '0')
                    this.setCellNumberFormat(worksheet, row, 2, '0')
                    this.setCellNumberFormat(worksheet, row, 3, '0')
                }
            }
        },
        setCellNumberFormat(worksheet, row, col, format) {
            const address = XLSX.utils.encode_cell({ r: row, c: col })
            const cell = worksheet[address]
            if (!cell) {
                return
            }

            cell.t = 'n'
            cell.z = format
        },
        exportColumnWidths() {
            if (this.mode === 'growth') {
                return [{ wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 12 }]
            }

            if (this.mode === 'lapse') {
                return [{ wch: 32 }, { wch: 22 }, { wch: 24 }, { wch: 12 }]
            }

            return [{ wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 10 }]
        },
        exportExcel() {
            const rows = this.buildExportRows()
            if (!rows.length) {
                return
            }

            const worksheet = XLSX.utils.json_to_sheet(rows)
            worksheet['!cols'] = this.exportColumnWidths()
            this.applyExcelFormats(worksheet)

            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, this.exportSheetName)
            XLSX.writeFile(workbook, this.exportFileName)
        },
    },
}
</script>

<style scoped>
.velocity-list--scrollable {
    max-height: 280px;
    overflow-y: auto;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.velocity-list--clickable {
    cursor: pointer;
    border-radius: 0.2rem;
}

.velocity-list--clickable:hover {
    background: rgba(0, 123, 255, 0.04);
}

.velocity-list--clickable:focus {
    outline: 0;
    box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.2);
}

.velocity-list__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.4rem 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.velocity-list__row--center-marker {
    background: rgba(0, 123, 255, 0.08);
}

.velocity-list__name {
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.velocity-list__value {
    font-weight: 700;
    white-space: nowrap;
    font-size: 0.9rem;
}

.velocity-list__footer {
    padding: 0 0.25rem;
}

.velocity-list__view-all {
    font-weight: 600;
}
</style>
